import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  Table,
} from "react-bootstrap";
import "../../assets/css/Details.css";
import {
  FaEdit,
  FaTrashAlt,
  FaKey,
  FaArrowLeft,
  FaFacebookMessenger,
  FaCheck,
} from "react-icons/fa";
import { usersAPI, employeesAPI } from "../../api/apiClient";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import LocationAutocomplete from "../LocationAutocomplete";

export default function UserProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const fromPage = routerLocation.state?.fromPage || 1;
  const userId = routerLocation.state?.userId;

  // 🔹 Loading states منفصلة لكل عملية
  const [loading, setLoading] = useState(true);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // 🔹 بيانات البروفايل الرئيسي
  const [profileData, setProfileData] = useState({
    id: "",
    firstname: "",
    lastname: "",
    nicname: "",
    company_name: "",
    email: "",
    website: "",
    telphone: "",
    tax_no: "",
    court: "",
    responsible_person: "",
    street_address: "",
    addressone: "",
    city: "",
    postal_code: "",
    is_confirmed: 0,
    employees: [],
  });

  // 🔹 بيانات تعديل البروفايل
  const [editData, setEditData] = useState({
    firstname: "",
    lastname: "",
    nicname: "",
    email: "",
    addressone: "",
    street_address: "",
    postal_code: "",
  });

  // 🔹 بيانات تعديل الموظف (منفصلة)
  const [editEmployeeData, setEditEmployeeData] = useState({
    id: null,
    firstname: "",
    lastname: "",
    nicname: "",
    email: "",
    addressone: "",
    street_address: "",
    postal_code: "",
  });

  const [passwordData, setPasswordData] = useState({
    password: "",
    password_confirmation: "",
  });

  // ─────────────────────────────────────────────────────────────
  // Fetch user details
  // ─────────────────────────────────────────────────────────────
  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await usersAPI.show(id);

      if (response.success) {
        const userData = response.data.data || {};
        setProfileData({
          ...profileData,
          ...userData,
          employees: Array.isArray(userData.employees) ? userData.employees : [],
        });
        setEmployees(
          Array.isArray(userData.employees) ? userData.employees : [],
        );
        setEditData({
          firstname: userData.firstname || "",
          lastname: userData.lastname || "",
          nicname: userData.nicname || "",
          email: userData.email || "",
          addressone: userData.addressone || "",
          street_address: userData.street_address || "",
          postal_code: userData.postal_code || "",
        });
      } else {
        setError(response.message || t("failed_to_fetch_user"));
      }
    } catch (err) {
      setError(t("failed_to_fetch_user"));
      console.error("Error fetching user:", err);
    } finally {
      setLoading(false);
    }
  }, [id, t]); // eslint-disable-line

  useEffect(() => {
    if (id) fetchUserDetails();
  }, [id, fetchUserDetails]);

  // ─────────────────────────────────────────────────────────────
  // Update main profile
  // ─────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!editData.email?.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("error"),
        text: t("email_required"),
      });
      return;
    }

    try {
      setUpdateLoading(true);
      const dataToSend = Object.fromEntries(
        Object.entries(editData).filter(
          ([_, value]) => value !== null && value !== "",
        ),
      );

      const response = await usersAPI.update(id, dataToSend);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("user_updated_successfully"),
          timer: 2500,
          showConfirmButton: false,
        });
        setShowEditModal(false);
        fetchUserDetails();
      } else {
        throw new Error(response.message || t("failed_to_update_user"));
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_user"),
        timer: 2500,
        showConfirmButton: false,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Confirm user
  // ─────────────────────────────────────────────────────────────
  const handleConfirmed = async () => {
    const result = await Swal.fire({
      title: t("are_you_sure"),
      text: t("confirm_user"),
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "rgba(51, 221, 57, 1)",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      setConfirmLoading(true);
      const response = await usersAPI.confirm(id);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("confirmed"),
          text: t("users_confirmed_successfully"),
          timer: 2500,
          showConfirmButton: false,
        });
        // ✅ تحديث الحالة فقط — بدون استبدال كل profileData
        setProfileData((prev) => ({ ...prev, is_confirmed: 1 }));
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_confirm_user"),
        timer: 2500,
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Change password
  // ─────────────────────────────────────────────────────────────
  const handlePasswordChange = async () => {
    setPasswordError("");

    if (!passwordData.password || passwordData.password.length < 8) {
      setPasswordError(t("password_must_be_at_least_8_characters"));
      return;
    }

    if (passwordData.password !== passwordData.password_confirmation) {
      setPasswordError(t("passwords_do_not_match"));
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await usersAPI.update(id, {
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation,
      });

      if (response.success) {
        setShowPasswordModal(false);
        setPasswordData({ password: "", password_confirmation: "" });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("password_updated_successfully"),
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        throw new Error(response.message || t("failed_to_update_password"));
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_password"),
        timer: 2500,
        showConfirmButton: false,
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Edit employee — open modal
  // ─────────────────────────────────────────────────────────────
  const handleOpenEditEmployee = (employee) => {
    setEditEmployeeData({
      id: employee.id,
      firstname: employee.firstname || employee.name || "",
      lastname: employee.lastname || "",
      nicname: employee.nicname || "",
      email: employee.email || "",
      addressone: employee.addressone || "",
      street_address: employee.street_address || "",
      postal_code: employee.postal_code || "",
    });
    setShowEditEmployeeModal(true);
  };

  const handleEditEmployeeInputChange = (e) => {
    const { name, value } = e.target;
    setEditEmployeeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmployeeLocationChange = (e) => {
    const { name, value, lat, lng } = e;
    setEditEmployeeData((prev) => ({
      ...prev,
      [name]: value,
      ...(lat && { [`${name}_lat`]: lat }),
      ...(lng && { [`${name}_lng`]: lng }),
    }));
  };

  // ─────────────────────────────────────────────────────────────
  // Update employee
  // ─────────────────────────────────────────────────────────────
  const handleEmployeeFormUpdate = async () => {
    if (!editEmployeeData.id) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("invalid_employee"),
      });
      return;
    }

    try {
      setUpdateLoading(true);

      const dataToSend = Object.fromEntries(
        Object.entries(editEmployeeData).filter(
          ([key, value]) =>
            key !== "id" && value !== null && value !== "",
        ),
      );

      const response = await employeesAPI.update(
        editEmployeeData.id,
        dataToSend,
      );

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("user_updated_successfully"),
          timer: 2500,
          showConfirmButton: false,
        });
        setShowEditEmployeeModal(false);
        fetchUserDetails();
      } else {
        throw new Error(response.message || t("failed_to_update_user"));
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_user"),
        timer: 2500,
        showConfirmButton: false,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Delete employee
  // ─────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!selectedEmployee) return;

    try {
      setDeleteLoading(true);
      const response = await employeesAPI.delete(selectedEmployee);

      if (response.success) {
        setShowDelete(false);
        setSelectedEmployee(null);
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("employee_deleted_successfully") || t("deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        fetchUserDetails();
      } else {
        throw new Error(response.message || t("failed_to_delete_employee"));
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_delete_employee"),
        timer: 2500,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Change handlers — منفصلة لكل modal
  // ─────────────────────────────────────────────────────────────
  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditUserLocationChange = (e) => {
    const { name, value, lat, lng } = e;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
      ...(lat && { [`${name}_lat`]: lat }),
      ...(lng && { [`${name}_lng`]: lng }),
    }));
  };

  const handleBack = () => {
    navigate("/admin/customers", {
      state: { fromPage, scrollToUser: userId },
    });
  };

  // ─────────────────────────────────────────────────────────────
  // Loading
  // ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container-wrapper">
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Header */}
        <div className="custom-header text-white p-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          <div className="d-flex align-items-center">
            <Button
              variant="light"
              className="me-3"
              onClick={handleBack}
              title={t("back_to_list")}
              type="button"
            >
              <FaArrowLeft />
            </Button>
            <h2 className="mb-0">
              {profileData.firstname} {profileData.lastname}
            </h2>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {profileData.is_confirmed === 0 && (
              <Button
                variant="info"
                onClick={handleConfirmed}
                disabled={confirmLoading}
                size="sm"
                type="button"
              >
                {confirmLoading ? (
                  <Spinner as="span" size="sm" animation="border" className="me-1" />
                ) : (
                  <FaCheck className="me-1" />
                )}
                {t("confirm_user")}
              </Button>
            )}
            <button
              className="btn btn-light"
              onClick={() => setShowPasswordModal(true)}
              type="button"
            >
              <FaKey className="me-1" />
              {t("change_password")}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowEditModal(true)}
              type="button"
            >
              <FaEdit className="me-1" />
              {t("edit_profile")}
            </button>
            <button
              className="btn btn-success"
              onClick={() =>
                navigate(`/admin/client-list/chat-client/${profileData.id}`)
              }
              type="button"
            >
              <FaFacebookMessenger className="me-1" />
            </button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="row mt-4">
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1">
                <strong>
                  {profileData.firstname} {profileData.lastname}
                </strong>
              </p>
              <p className="mb-1">
                <b>{t("company_name")}: </b>
                {profileData.company_name || "-"}
              </p>
              <p className="mb-1">
                <b>{t("nachname")}: </b>
                {profileData.nicname || "-"}
              </p>
              <p className="mb-1">
                <b>{t("Email")}:</b> {profileData.email || "-"}
              </p>
              <p className="mb-1">
                <b>{t("phone")}:</b> {profileData.telphone || "-"}
              </p>
              <p className="mb-1">
                <b>{t("website")}:</b>{" "}
                {profileData.website ? (
                  <a
                    href={profileData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profileData.website}
                  </a>
                ) : (
                  "-"
                )}
              </p>
              <p className="mb-1">
                <b>{t("VAT/Tax_No")}:</b> {profileData.tax_no || "-"}
              </p>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1">
                <b>{t("local_court")}:</b> {profileData.court || "-"}
              </p>
              <p className="mb-1">
                <b>{t("responsible_person")}:</b>{" "}
                {profileData.responsible_person || "-"}
              </p>
              <p className="mb-1">
                <b>{t("street_address")}:</b>{" "}
                {profileData.street_address || "-"}
              </p>
              <p className="mb-1">
                <b>{t("addressone")}:</b> {profileData.addressone || "-"}
              </p>
              <p className="mb-1">
                <b>{t("postal_code")}:</b> {profileData.postal_code || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Employees Table */}
        <div className="card mt-4 p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">👥 {t("employees")}</h5>
          </div>

          {employeesLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">{t("loading_employees")}</p>
            </div>
          ) : employees.length === 0 ? (
            <Alert variant="info">{t("no_employees")}</Alert>
          ) : (
            <div className="table-responsive">
              <Table bordered hover className="align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>{t("name")}</th>
                    <th>{t("email")}</th>
                    <th>{t("phone")}</th>
                    <th>{t("street_address")}</th>
                    <th>{t("postal_code")}</th>
                    <th>{t("booking.bookings_count")}</th>
                    <th>{t("employeesTable.can_create_orders")}</th>
                    <th>{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee, index) => {
                    const displayName =
                      employee.name ||
                      `${employee.firstname || ""} ${
                        employee.lastname || ""
                      }`.trim() ||
                      employee.nicname ||
                      "-";

                    return (
                      <tr key={employee.id}>
                        <td>{index + 1}</td>
                        <td
                          onClick={() => navigate(`/admin/employees/details/${employee.id}`)}
                          style={{ cursor: employee ? "pointer" : "default", color: employee ? "#0d6efd" : "inherit" }}
                        >
                          {displayName}</td>
                        <td>{employee.email || "-"}</td>
                        <td>{employee.telphone || "-"}</td>
                        <td>{employee.street_address || "-"}</td>
                        <td>{employee.postal_code || "-"}</td>
                        <td>{employee.all_bookings_count || 0}</td>
                        <td>
                          {employee.can_create_orders
                            ? t("yes")
                            : t("no")}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            className="me-2"
                            type="button"
                            onClick={() => handleOpenEditEmployee(employee)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            type="button"
                            onClick={() => {
                              setSelectedEmployee(employee.id);
                              setShowDelete(true);
                            }}
                            title={t("Delete")}
                          >
                            <FaTrashAlt />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>

        {/* ─────────────────────────────────────────────────────────
            Password Modal
        ───────────────────────────────────────────────────────── */}
        <Modal
          show={showPasswordModal}
          onHide={() => {
            setShowPasswordModal(false);
            setPasswordError("");
            setPasswordData({ password: "", password_confirmation: "" });
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("change_password")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {passwordError && <Alert variant="danger">{passwordError}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t("new_password")}</Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  value={passwordData.password}
                  onChange={(e) => {
                    setPasswordData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }));
                    setPasswordError("");
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t("confirm_password")}</Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => {
                    setPasswordData((prev) => ({
                      ...prev,
                      password_confirmation: e.target.value,
                    }));
                    setPasswordError("");
                  }}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordError("");
                setPasswordData({ password: "", password_confirmation: "" });
              }}
              disabled={passwordLoading}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handlePasswordChange}
              disabled={passwordLoading}
            >
              {passwordLoading ? (
                <>
                  <Spinner as="span" size="sm" animation="border" className="me-2" />
                  {t("Save...")}
                </>
              ) : (
                t("save")
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ─────────────────────────────────────────────────────────
            Edit User Modal
        ───────────────────────────────────────────────────────── */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("edit_user")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("first_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    value={editData.firstname || ""}
                    onChange={handleEditUserChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("last_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    value={editData.lastname || ""}
                    onChange={handleEditUserChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t("nicname")}</Form.Label>
              <Form.Control
                type="text"
                name="nicname"
                value={editData.nicname || ""}
                onChange={handleEditUserChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("email")} *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editData.email || ""}
                onChange={handleEditUserChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("street_address")}</Form.Label>
              <Form.Control
                type="text"
                name="street_address"
                value={editData.street_address || ""}
                onChange={handleEditUserChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("address_line_2")}</Form.Label>
              <Form.Control
                type="text"
                name="addressone"
                value={editData.addressone || ""}
                onChange={handleEditUserChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editUserPostalCode">
              <Form.Label>{t("postal_code")}</Form.Label>
              <LocationAutocomplete
                name="postal_code"
                value={editData.postal_code || ""}
                onChange={handleEditUserLocationChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowEditModal(false)}
              disabled={updateLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleUpdate}
              disabled={updateLoading}
            >
              {updateLoading ? (
                <>
                  <Spinner as="span" size="sm" animation="border" className="me-2" />
                  {t("updating")}
                </>
              ) : (
                t("save")
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ─────────────────────────────────────────────────────────
            Edit Employee Modal
        ───────────────────────────────────────────────────────── */}
        <Modal
          show={showEditEmployeeModal}
          onHide={() => setShowEditEmployeeModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("edit_user")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("first_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    value={editEmployeeData.firstname || ""}
                    onChange={handleEditEmployeeInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("last_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    value={editEmployeeData.lastname || ""}
                    onChange={handleEditEmployeeInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t("nicname")}</Form.Label>
              <Form.Control
                type="text"
                name="nicname"
                value={editEmployeeData.nicname || ""}
                onChange={handleEditEmployeeInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("email")} *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editEmployeeData.email || ""}
                onChange={handleEditEmployeeInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("street_address")}</Form.Label>
              <Form.Control
                type="text"
                name="street_address"
                value={editEmployeeData.street_address || ""}
                onChange={handleEditEmployeeInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("address_line_2")}</Form.Label>
              <Form.Control
                type="text"
                name="addressone"
                value={editEmployeeData.addressone || ""}
                onChange={handleEditEmployeeInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editEmployeePostalCode">
              <Form.Label>{t("postal_code")}</Form.Label>
              <LocationAutocomplete
                name="postal_code"
                value={editEmployeeData.postal_code || ""}
                onChange={handleEmployeeLocationChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowEditEmployeeModal(false)}
              disabled={updateLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleEmployeeFormUpdate}
              disabled={updateLoading}
            >
              {updateLoading ? (
                <>
                  <Spinner as="span" size="sm" animation="border" className="me-2" />
                  {t("updating")}
                </>
              ) : (
                t("save")
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ─────────────────────────────────────────────────────────
            Delete Employee Modal
        ───────────────────────────────────────────────────────── */}
        <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("delete_employee")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t("confirm_delete_employee")}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowDelete(false);
                setSelectedEmployee(null);
              }}
              disabled={deleteLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={confirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <Spinner as="span" size="sm" animation="border" className="me-2" />
                  {t("deleting")}
                </>
              ) : (
                t("delete")
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}